import "@astryxdesign/core/reset.css";
import "@astryxdesign/core/astryx.css";
import { ToastViewport } from "@astryxdesign/core/Toast";
import { Theme } from "@astryxdesign/core/theme";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import type { IdeaCreate, IdeaPatch } from "../shared/types";
import { api, ApiError } from "./lib/api";
import { router } from "./router";
import { hqTheme } from "./theme";

registerSW({ immediate: true });

function onAuthError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    void router.navigate({ to: "/login" });
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
});

// Offline queue: mutations fired while offline pause (networkMode "online"),
// persist to localStorage, and replay on reconnect / next launch. Defaults
// live here because persisted mutations rehydrate without component closures.
queryClient.setMutationDefaults(["createIdea"], {
  mutationFn: (body: IdeaCreate) => api.createIdea(body),
});
queryClient.setMutationDefaults(["patchIdea"], {
  mutationFn: (vars: { id: string; patch: IdeaPatch }) => api.patchIdea(vars.id, vars.patch),
});
queryClient.setMutationDefaults(["deleteIdea"], {
  mutationFn: (id: string) => api.deleteIdea(id),
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "hq-cache",
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Theme theme={hqTheme} mode="dark">
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, maxAge: 7 * 24 * 60 * 60 * 1000, buster: "v1" }}
        onSuccess={() => {
          void queryClient.resumePausedMutations();
        }}
      >
        <ToastViewport position="bottomEnd">
          <RouterProvider router={router} />
        </ToastViewport>
      </PersistQueryClientProvider>
    </Theme>
  </StrictMode>,
);
